import { CalcVsType } from '@/store/store_types'
import type { EVNavRadarResponse } from '@/types/ev_nav_types'
import { timeWithDCCurve } from '@/utils/calcChargingTime'
import type { TripAcDcSplit } from '@/utils/calcTripAcDcSplit'
import generateUniqueLocalID from '@/utils/generateUniqueLocalID'
import { kWhToHydrogenKgs } from '@/utils/hydrogenUtils'
import to2DP from '@/utils/to2DP'
import Vehicle from '../vehicle_classes/vehicle'
import type { TripBaseLeg } from './trip'

interface TripComparisonOptions {
  /** The vehicle being compared against. */
  vehicle: Vehicle

  /** EV nav radar data response for this vehicle doing the comparison trip */
  energyData: EVNavRadarResponse

  /** Base legs used to calculate this comparisons energy usage. */
  baseLegs: TripBaseLeg[]

  /**
   * The minimum state of charge the user wants to let the charge drop to before charging.
   *
   * NOTE: in the format of a decimal representation of a percentage e.g. 80% = 0.8
   */
  SOCMin: number

  /**
   * The maximum state of charge the user wants to charge to at charging stops.
   *
   * NOTE: in the format of a decimal representation of a percentage e.g. 80% = 0.8
   */
  SOCMax: number

  /** AC DC Split for this comparison if there is one. */
  acDCSplit?: TripAcDcSplit

  /** Whether this comparison is a one of the recommended vehicles for this trip. */
  isRecommendation?: boolean

  /** The ranking of the recommendation if it is a recommendation. */
  recommendationRanking?: number
}

interface TripComparisonEstimatedChargingTime {
  /** Minimum charge rate. */
  ac_charging_rate: number

  /** Maximum charge rate. */
  dc_charging_rate: number

  /** Estimated charging time at min charge rate. */
  ac_charging_time: number

  /** Estimated charging time at max charge rate. */
  dc_charging_time: number
}

export default class TripComparison {
  // ----------------------------------------------------------------------- //
  // -------------------------- Global class state ------------------------- //
  // ----------------------------------------------------------------------- //

  /** global record of class instance ids this session. */
  static usedIds: string[] = []

  // ----------------------------------------------------------------------- //
  // ------------------------------- State --------------------------------- //
  // ----------------------------------------------------------------------- //

  /** Unique id for this instance. */
  localId: string

  /** The vehicle being compared against. */
  vehicle: Vehicle

  /** EV nav radar data response for this vehicle doing the comparison trip */
  energyData: EVNavRadarResponse

  /** Base legs used to calculate this comparisons energy usage. */
  baseLegs: TripBaseLeg[]

  /**
   * The minimum state of charge the user wants to let the charge drop to before charging.
   *
   * NOTE: in the format of a decimal representation of a percentage e.g. 80% = 0.8
   */
  SOCMin = 0

  /**
   * The maximum state of charge the user wants to charge to at charging stops.
   *
   * NOTE: in the format of a decimal representation of a percentage e.g. 80% = 0.8
   */
  SOCMax = 1

  /** AC DC Split for this comparison if there is one. */
  acDCSplit?: TripAcDcSplit

  /** Whether this comparison is a recommendation. */
  readonly isRecommendation: boolean

  /**
   * The ranking of this recommendation.
   *
   * Note: this is only set if this is a recommendation
   */
  readonly recommendationRanking?: number

  // ----------------------------------------------------------------------- //
  // ---------------------------- Constructor ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Constructs a new instance of the TripComparison class.
   *
   * @param {TripComparisonOptions} options - The options for creating the instance.
   * @throws {Error} If the vehicle does not have an EV model provided.
   * @throws {Error} If the chargeUsableInRange is not between 0 and 1.
   */
  constructor(options: TripComparisonOptions) {
    // check vehicle has an EV model to compare.
    if (!options.vehicle.evModel) throw new Error('Vehicle has no EV model provided')
    // check that chargeUsableInRange is between 0 and 1
    if ((options.SOCMin && options.SOCMin < 0) || (options.SOCMax && options.SOCMax > 1))
      throw new Error('Invalid chargeUsableInRange provided')
    // check if is recommendation it has a ranking
    if (options.isRecommendation && !options.recommendationRanking)
      throw new Error('Recommendation must have a ranking')

    // set class state
    this.localId = generateUniqueLocalID(TripComparison.usedIds, 'trip-comparison')
    this.energyData = options.energyData
    this.vehicle = options.vehicle
    this.baseLegs = options.baseLegs
    this.SOCMin = options.SOCMin
    this.SOCMax = options.SOCMax
    this.acDCSplit = options.acDCSplit
    this.isRecommendation = options.isRecommendation ?? false
    this.recommendationRanking = options.isRecommendation
      ? options.recommendationRanking
      : undefined

    // add to global class state
    if (!TripComparison.usedIds.includes(this.localId)) {
      TripComparison.usedIds.push(this.localId)
    }
  }

  // ----------------------------------------------------------------------- //
  // ------------------------------- Getters ------------------------------- //
  // ----------------------------------------------------------------------- //

  /**
   * Returns the total driving distance in meters.
   *
   * @return {number} The driving distance in meters.
   */
  public get drivingDistance(): number {
    return Math.round(this.energyData.Distance)
  }

  /**
   * Returns the driving time in number of seconds.
   *
   * @return {number} The driving time in seconds.
   */
  public get drivingTime(): number {
    return Math.round(this.baseLegs.reduce((a, b) => a + b.time, 0))
  }

  /**
   * Decimal representation of charge usable in the drivers selected min charge
   * to max charge range
   *
   * This should be a number between 0 and 1.
   */
  public get chargeUsableInRange(): number {
    return this.SOCMax - this.SOCMin
  }

  /**
   * Calculates and returns an object with the estimated AC and DC charging
   * times and rates for the trip.
   *
   * @return {TripComparisonEstimatedChargingTime} An object with the estimated
   *   AC and DC charging rates and times for the trip.
   */
  public get estimatedChargingTime(): TripComparisonEstimatedChargingTime {
    // calculate the AC and DC charging rates:

    // calculate DC charging rate:

    // get vehicle dc charging rate in kW
    const vehicle_dc_charging_rate = this.vehicle.evModel!.maxElectricPowerDC // ev model checked in constructor
    // get charger dc charging rates in W
    const charger_dc_charging_rates: number[] = []
    // loop through chargers adding their dc charging rates to array
    this.energyData.Chargers.forEach((charger) => {
      charger.Ports?.forEach((port) => {
        if (port.PowerType === 'DC') {
          charger_dc_charging_rates.push(port.MaxElectricPower) // note: port.MaxElectricPower is in W
        }
      })
    })
    // sort dc charging rates by fastest to slowest in W
    charger_dc_charging_rates.sort((a, b) => b - a) // sort in descending order
    /**
     * get first 10 DC chargers in W, as fast chargers are preferred by evNav
     * this limits the pool to the top 10 to closer simulate the more complex
     * calculation made in a full evNav route plan.
     */
    const top_10_charger_dc_charging_rates = charger_dc_charging_rates.slice(0, 10)
    // get average of top 10 DC chargers in W
    const average_charger_dc_charging_rate =
      top_10_charger_dc_charging_rates.reduce((a, b) => a + b, 0) /
      top_10_charger_dc_charging_rates.length
    // take min of charger_dc_charging_rate (converted to kW) or vehicle_dc_charging_rate
    const dc_charging_rate = Math.min(
      average_charger_dc_charging_rate / 1000,
      vehicle_dc_charging_rate,
    )

    // calculate AC charging rate:

    const ac_charging_rate = this.vehicle.evModel!.maxElectricPowerAC // ev model checked in constructor

    // check if can be made on a single charge
    if (this.numberOfChargingStopsNeeded <= 1) {
      // compile return, no charging time needed, return 0 for both AC and DC
      return {
        ac_charging_rate,
        dc_charging_rate,
        ac_charging_time: 0, // can complete on single charge no charging time needed
        dc_charging_time: 0, // can complete on single charge no charging time needed
      }
    }

    // calculate single charge DC charging time in hours
    const dcChargingTime = timeWithDCCurve(
      this.SOCMin * this.vehicle.totalBatteryKWh(),
      this.SOCMax * this.vehicle.totalBatteryKWh(),
      this.vehicle.totalBatteryKWh(),
      dc_charging_rate,
    )

    // calculate total DC charging time for trip in hours
    const totalDcChargingTime = (dcChargingTime ?? 0) * this.numberOfChargingStopsNeeded

    // compile return
    return {
      ac_charging_rate,
      dc_charging_rate,
      ac_charging_time: Math.round((this.totalEnergy / ac_charging_rate) * 3600), // convert to seconds
      dc_charging_time: Math.round(totalDcChargingTime * 3600), // convert to seconds
    }
  }

  /**
   * Returns the total energy in needed to be consumed during the trip in kWh.
   *
   * @return {number} The total energy in kWh.
   */
  public get totalEnergy(): number {
    return this.energyData.Energy
  }

  /**
   * Calculates the total charge used during the trip.
   *
   * note: this can be more than 100% charge implying that the trip can not be
   * done on a single charge.
   *
   * @return {number} The total charge.
   */
  public get totalChargeUsed(): number {
    return this.totalEnergy / this.vehicle.totalBatteryKWh()
  }

  /**
   * Calculates the number of charging stops needed based on the total charge
   * used and the charge usable in range.
   *
   * @return {number} The number of charging stops needed.
   */
  public get numberOfChargingStopsNeeded(): number {
    return this.totalChargeUsed / this.chargeUsableInRange
  }

  /**
   * Calculates the cost of home charging based on the private charging cost
   * and the total energy used.
   *
   * @param {number} privateChargingCost The cost of private charging per kWh.
   * @return {number} The cost of home charging.
   */
  public homeChargingCost(privateChargingCost: number): number {
    return privateChargingCost * this.totalEnergy
  }

  /**
   * Calculates the cost of fast charging based on the public charging cost and
   * the total energy used.
   *
   * @param {Object} options - An object containing the following properties:
   *   @param {number} publicChargingCost - The cost of public charging per kWh.
   *   @param {number} publicChargingCostPerMin - The cost of public charging per minute.
   * @return {number} The cost of fast charging.
   */
  public fastChargingCost({
    publicChargingCost,
    publicChargingCostPerMin,
  }: {
    publicChargingCost: number
    publicChargingCostPerMin: number
  }): number {
    // Calculate the energy cost
    const energyCost = publicChargingCost * this.totalEnergy

    // Calculate the cost for charging time
    const minsCharging = this.estimatedChargingTime.dc_charging_time / 60
    const costForTime = minsCharging * publicChargingCostPerMin

    // Return the total cost of fast charging
    return energyCost + costForTime
  }

  /**
   * Calculates the equivalent fuel cost based on the fuel cost per litre and
   * the kilometres per litre.
   *
   * @param {number} fuelCostPerLitre - The cost of fuel per litre.
   * @param {number} kmPerLitre - The kilometres per litre.
   * @return {number} The equivalent fuel cost.
   */
  public equivalentFuelCost({
    fuelCostPerLitre,
    kmPerLitre,
  }: {
    fuelCostPerLitre: number
    kmPerLitre: number
  }): number {
    // n = distance km * ev model equivalent fuel * fuelCostPerLitre
    // n = (metersDriven / metersPerLitre) * fuelCostPerLitre
    return (this.drivingDistance / (kmPerLitre * 1000)) * fuelCostPerLitre
  }

  /**
   * Calculates the total road user charges based on the cost per 1000 kilometres
   * and the driving distance.
   *
   * @param {number} roadUserChargesCost - The cost of road user charges per 1000 kilometres.
   * @return {number} The total road user charges.
   */
  public roadUserCharges(roadUserChargesCost: number): number {
    const drivenKm = this.drivingDistance / 1000
    const numberOf1000km = drivenKm / 1000
    return roadUserChargesCost * numberOf1000km
  }

  /**
   * Calculates the avoided CO2 emissions for a trip based on the fuel type and efficiency.
   *
   * @param {CalcVsType} calcVs - The type of fuel used for calculation.
   * @param {number} petrolKmPerLitre - The kilometres per litre for petrol.
   * @param {number} dieselKmPerLitre - The kilometres per litre for diesel.
   * @return {number | undefined} The avoided CO2 emissions in 2 decimal places, or undefined if the vehicle is an ICE vehicle.
   */
  public calcAvoidedCO2({
    calcVs,
    petrolKmPerLitre,
    dieselKmPerLitre,
    calcAsHydrogen = false,
    hydrogenCO2PerKg,
    electricityCO2PerKWh,
  }: {
    calcVs: CalcVsType
    petrolKmPerLitre: number
    dieselKmPerLitre: number
    calcAsHydrogen?: boolean
    hydrogenCO2PerKg: number
    electricityCO2PerKWh: number
  }): number | undefined {
    if (this.vehicle?.fuelType === 'Petrol' || this.vehicle?.fuelType === 'Diesel') return // no need to calculate avoided CO2 for a ICE vehicle.

    // emitted CO₂ from production = kWh * co2PerKWh || kgHydrogen * co2PerKg
    const emissionsFromProduction = calcAsHydrogen
      ? kWhToHydrogenKgs(this.totalEnergy) * hydrogenCO2PerKg
      : this.totalEnergy * electricityCO2PerKWh

    // efficiency = kilometres of travel per litre of fuel consumed.
    // user petrol or diesel km travelled per litre of fuel consumed
    const efficiency = calcVs === CalcVsType.PETROL ? petrolKmPerLitre : dieselKmPerLitre

    // co2PerLitre = CO₂ emitted per litre of fuel used including refinement
    // 2.8 for petrol and 3.2 for diesel
    const co2PerLitre = calcVs === CalcVsType.PETROL ? 2.8 : 3.2

    // distance in kilometres
    const distance_km = this.drivingDistance / 1000

    // avoidedCO₂ = ((distance in kilometres / efficiency) * co2PerLitre) - (emitted CO₂ from production)
    return to2DP((distance_km / efficiency) * co2PerLitre - emissionsFromProduction)
  }
}
